import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { BASE_URL, breadcrumbLD, ROUTES } from '../seo/routeMetadata';
import { MARKET_CATEGORIES } from '../seo/seoContent';
import { slugify } from '../seo/slugify';
import { apiService } from '../services/api';

/**
 * Serves BOTH /markets (hub) and /markets/:category.
 * Route hub falls back to null-category branch.
 */
const MarketCategoryPage = ({ isHub = false }) => {
  const { category: paramCategory } = useParams();
  const cat = MARKET_CATEGORIES[paramCategory];
  const invalidCategory = !isHub && !cat;

  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (invalidCategory) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiService.getMarkets(null, cat ? cat.categoryKey : null, null, 12);
        if (!cancelled) setMarkets(Array.isArray(data) ? data : (data.markets || []));
      } catch (e) {
        // Non-fatal — page still renders content sections.
        if (!cancelled) setMarkets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cat, invalidCategory]);

  if (invalidCategory) {
    return <Navigate to="/markets" replace />;
  }

  const meta = isHub ? ROUTES['/markets'] : {
    title: cat.title,
    description: cat.aeoAnswer,
    h1: cat.h1,
    aeoAnswer: cat.aeoAnswer,
    sections: cat.sections.map((s) => ({ h2: s.h2, body: s.p })),
    path: `/markets/${paramCategory}`,
  };

  const crumbs = isHub
    ? [{ name: 'Home', path: '/' }, { name: 'Markets', path: '/markets' }]
    : [{ name: 'Home', path: '/' }, { name: 'Markets', path: '/markets' }, { name: cat.h1, path: `/markets/${paramCategory}` }];

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title={meta.title}
        description={meta.description}
        url={`${BASE_URL}${meta.path}`}
        jsonLd={[breadcrumbLD(crumbs)]}
      />
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-10 pb-8 border-b border-[#1e2a45]">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-gray-500">
            {crumbs.map((c, i) => (
              <span key={c.path}>
                {i > 0 && <span className="mx-2 text-gray-600">›</span>}
                {i === crumbs.length - 1 ? (
                  <span className="text-gray-400">{c.name}</span>
                ) : (
                  <Link to={c.path} className="hover:text-white">{c.name}</Link>
                )}
              </span>
            ))}
          </nav>

          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4" data-testid="page-h1">{meta.h1}</h1>
          <p className="text-lg text-gray-200 leading-relaxed max-w-3xl">{meta.aeoAnswer}</p>
        </header>

        {isHub && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-12">
            {Object.entries(MARKET_CATEGORIES).map(([slug, c]) => (
              <Link
                key={slug}
                to={`/markets/${slug}`}
                data-testid={`category-card-${slug}`}
                className="group block rounded-2xl border border-[#1e2a45] bg-[#131b2e] hover:border-[#3B82F6]/50 p-5 transition-colors"
              >
                <h2 className="text-lg font-semibold text-white mb-2 group-hover:text-[#3B82F6]">{c.h1}</h2>
                <p className="text-sm text-gray-400 line-clamp-2">{c.aeoAnswer}</p>
              </Link>
            ))}
          </div>
        )}

        {!isHub && (
          <>
            <div className="space-y-6 mb-12">
              {meta.sections.map((s) => (
                <section key={s.h2}>
                  <h2 className="text-xl font-semibold text-white mb-2">{s.h2}</h2>
                  <p className="text-gray-300 leading-relaxed">{s.body}</p>
                </section>
              ))}
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-4">Live {meta.h1.replace(' Prediction Markets','')} markets</h2>
              {loading ? (
                <p className="text-gray-400 text-sm">Loading markets…</p>
              ) : markets.length === 0 ? (
                <p className="text-gray-400 text-sm">No live markets in this category right now. Check back soon — new markets are added continuously.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {markets.slice(0, 12).map((m) => (
                    <Link
                      key={m.id}
                      to={`/markets/${paramCategory}/${slugify(m.title)}`}
                      className="block rounded-xl border border-[#1e2a45] bg-[#131b2e] hover:bg-[#1a2440] p-4"
                    >
                      <div className="text-sm font-medium text-white line-clamp-2">{m.title}</div>
                      <div className="mt-2 text-xs text-gray-500">YES {Math.round(m.yes_price || 50)}¢ · NO {100 - Math.round(m.yes_price || 50)}¢</div>
                    </Link>
                  ))}
                </div>
              )}
              <div className="mt-6">
                <Link to={`/?category=${cat.categoryKey}`} className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline text-sm">
                  See all live markets <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </section>
          </>
        )}

        <div className="mt-12 pt-8 border-t border-[#1e2a45]">
          <h3 className="text-lg font-semibold text-white mb-4">Learn more</h3>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/learn" className="text-[#3B82F6] hover:underline">All learning articles →</Link>
            <Link to="/how-it-works" className="text-[#3B82F6] hover:underline">How AMP Predict works →</Link>
            <Link to="/learn/how-yes-no-event-contracts-work" className="text-[#3B82F6] hover:underline">How YES/NO contracts work →</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MarketCategoryPage;
